import React from "react";
import {
	Field,
	FieldLabel,
	FieldVal,
	RangeInput,
	SegRow,
	SegItem,
	SwatchRow,
	Swatch,
	SwatchPickWrap,
	SwatchColorInput,
	StyledSelect,
} from "./inspectorStyled";

// ---------------------------------------------------------------------------
// RangeField
// ---------------------------------------------------------------------------

interface RangeFieldProps {
	label: string;
	value: number;
	min: number;
	max: number;
	onChange: (v: number) => void;
}

export function RangeField({ label, value, min, max, onChange }: RangeFieldProps): JSX.Element {
	return (
		<Field>
			<FieldLabel>
				{label}
				<FieldVal>{value}px</FieldVal>
			</FieldLabel>
			<RangeInput
				type="range"
				min={min}
				max={max}
				value={value}
				onChange={(e) => onChange(Number(e.target.value))}
			/>
		</Field>
	);
}

// ---------------------------------------------------------------------------
// SegField
// ---------------------------------------------------------------------------

interface SegFieldProps {
	label: string;
	options: { value: string; label: string }[];
	value: string;
	onChange: (v: string) => void;
}

export function SegField({ label, options, value, onChange }: SegFieldProps): JSX.Element {
	return (
		<Field>
			<FieldLabel>{label}</FieldLabel>
			<SegRow>
				{options.map((opt) => (
					<SegItem
						key={opt.value}
						active={opt.value === value}
						onClick={() => onChange(opt.value)}>
						{opt.label}
					</SegItem>
				))}
			</SegRow>
		</Field>
	);
}

// ---------------------------------------------------------------------------
// SwatchField — preset color swatches + RGB custom picker
// ---------------------------------------------------------------------------

interface SwatchFieldProps {
	label: string;
	presets: string[];
	value: string;
	onChange: (v: string) => void;
}

export function SwatchField({ label, presets, value, onChange }: SwatchFieldProps): JSX.Element {
	const safePickerVal = /^#[0-9a-fA-F]{6}$/.test(value || "") ? value : "#005e9e";

	return (
		<Field>
			<FieldLabel>{label}</FieldLabel>
			<SwatchRow>
				{presets.map((c) => (
					<Swatch
						key={c}
						style={{ background: c }}
						active={c === value}
						title={c}
						onClick={() => onChange(c)}
					/>
				))}
				<SwatchPickWrap title="Chọn màu bất kỳ (RGB)">
					<SwatchColorInput
						type="color"
						value={safePickerVal}
						onChange={(e) => onChange(e.target.value)}
					/>
				</SwatchPickWrap>
			</SwatchRow>
		</Field>
	);
}

// ---------------------------------------------------------------------------
// FontField — select dropdown
// ---------------------------------------------------------------------------

const FONT_OPTIONS = [
	{ value: "system", label: "Hệ thống (Sans)" },
	{ value: "arial", label: "Arial" },
	{ value: "georgia", label: "Georgia (Serif)" },
	{ value: "verdana", label: "Verdana" },
	{ value: "tahoma", label: "Tahoma" },
];

interface FontFieldProps {
	label: string;
	value: string;
	onChange: (v: string) => void;
}

export function FontField({ label, value, onChange }: FontFieldProps): JSX.Element {
	return (
		<Field>
			<FieldLabel>{label}</FieldLabel>
			<StyledSelect
				value={value}
				onChange={(e) => onChange(e.target.value)}>
				{FONT_OPTIONS.map((o) => (
					<option
						key={o.value}
						value={o.value}>
						{o.label}
					</option>
				))}
			</StyledSelect>
		</Field>
	);
}

// ---------------------------------------------------------------------------
// LabelField — read-only display label
// ---------------------------------------------------------------------------

interface LabelFieldProps {
	label: string;
	value: string;
}

export function LabelField({ label, value }: LabelFieldProps): JSX.Element {
	return (
		<Field>
			<FieldLabel>{label}</FieldLabel>
			<FieldVal>{value}</FieldVal>
		</Field>
	);
}
